// Global Instructions Rule Applied!
// Shape returned by fund__fin_year_allocation, the read of the fund Ledger
// tab's Close year dialog (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md 4.2):
// the curative partner allocation of a financial year per partner and K-1
// tax category - Calculated / Override / effective and the carry-forward -
// the categories' totals, the accounts without a tax category that refuse
// the close, and whether the close can book. A fund on Static has no rows.
// Mirrors backend/src/data_manager/data_models/fund_fin_year_allocation_record.py.
// Ids are plain strings (Dataverse GUIDs fail zod's .uuid()).
import { z } from 'zod'

export const FinYearAllocationCellSchema = z
  .object({
    tax_categories__id: z.number().int().nullish(),
    prog__pro_rata: z.number(),
    prog__pro_rata_fmt: z.string().nullish(),
    prog__calculated: z.number(),
    prog__calculated_fmt: z.string().nullish(),
    prog__override: z.number().nullish(),
    prog__effective: z.number(),
    prog__effective_fmt: z.string().nullish(),
    prog__curative: z.number(),
    prog__curative_fmt: z.string().nullish(),
    prog__carry_forward: z.number(),
    prog__carry_forward_fmt: z.string().nullish()
  })
  .strict()

export type FinYearAllocationCell = z.infer<typeof FinYearAllocationCellSchema>

export const FinYearAllocationRowSchema = z
  .object({
    investors__id: z.string(),
    investors__name: z.string(),
    prog__share: z.number(),
    cells: z.array(FinYearAllocationCellSchema)
  })
  .strict()

export type FinYearAllocationRow = z.infer<typeof FinYearAllocationRowSchema>

export const FinYearAllocationCategorySchema = z
  .object({
    tax_categories__id: z.number().int().nullish(),
    tax_categories__code: z.string(),
    tax_categories__label: z.string(),
    prog__period_total: z.number(),
    prog__period_total_fmt: z.string().nullish(),
    prog__effective_total: z.number(),
    prog__effective_total_fmt: z.string().nullish(),
    prog__balanced: z.boolean()
  })
  .strict()

export type FinYearAllocationCategory = z.infer<typeof FinYearAllocationCategorySchema>

export const FinYearAllocationOutstandingSchema = z
  .object({
    investors__id: z.string(),
    investors__name: z.string(),
    tax_categories__id: z.number().int().nullish(),
    tax_categories__label: z.string(),
    prog__carry_forward: z.number(),
    prog__carry_forward_fmt: z.string().nullish()
  })
  .strict()

export type FinYearAllocationOutstanding = z.infer<typeof FinYearAllocationOutstandingSchema>

export const FinYearAllocationUnmappedSchema = z
  .object({
    acc_gl_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string(),
    prog__balance: z.number(),
    prog__balance_fmt: z.string().nullish()
  })
  .strict()

export const FundFinYearAllocationSchema = z
  .object({
    fin_year: z
      .object({
        id: z.string(),
        fin_year: z.number().int(),
        year_start_date: z.string().nullish(),
        year_end_date: z.string().nullish(),
        closed: z.boolean().nullish()
      })
      .strict(),
    per_fin_year: z.boolean(),
    basis_label: z.string(),
    is_booked: z.boolean(),
    can_close: z.boolean(),
    unmapped_accounts: z.array(FinYearAllocationUnmappedSchema),
    categories: z.array(FinYearAllocationCategorySchema),
    rows: z.array(FinYearAllocationRowSchema),
    outstanding: z.array(FinYearAllocationOutstandingSchema),
    line_count: z.number().int(),
    status_message: z.string().nullish()
  })
  .strict()

export type FundFinYearAllocation = z.infer<typeof FundFinYearAllocationSchema>

/** One override the Close year dialog sends back with the close. */
export interface FinYearAllocationOverride {
  investor_id: string
  tax_category_id: number | null
  override_amount: number | null
}

export const validateFundFinYearAllocation = (data: unknown): FundFinYearAllocation => {
  const result = FundFinYearAllocationSchema.safeParse(data)
  if (!result.success) {
    throw new Error(`Fund financial year allocation validation failed: ${result.error.message}`)
  }
  return result.data
}
